import React, { useEffect, useMemo, useState } from "react";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { createCheckoutSession, getPaymentPacks } from "../api/paymentApi";
import { useI18n } from "../i18n";
import { trackModalEvent, trackPaymentEvent } from "../utils/analytics";
import { stripePromise } from "../utils/stripe";
import "./BuyUnitsModal.css";
const price = (p) => `${(Number(p.priceAmountMinor || 0) / 100).toFixed(2)} ${p.currency || "PLN"}`;
function BuyUnitsModal({ isOpen, onClose, initialPackCode = null }) {
  const { locale } = useI18n();
  const [packs, setPacks] = useState([]), [selectedCode, setSelectedCode] = useState(initialPackCode), [clientSecret, setClientSecret] = useState(""), [error, setError] = useState(""), [loading, setLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(() => stripePromise ? "loading" : "unconfigured");
  useEffect(() => {
    if (!stripePromise) return undefined;
    let mounted = true;
    stripePromise.then((stripe) => {
      if (mounted) setStripeStatus(stripe ? "ready" : "failed");
    });
    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    if (!isOpen) return;
    trackModalEvent("opened", "buy_units");
    setClientSecret("");
    setError("");
    getPaymentPacks()
      .then(({ data }) => {
        const p = Array.isArray(data) ? data : [];
        setPacks(p);
        const nextCode = initialPackCode || p.find((x) => x.kind === "CHAT_UNITS")?.packCode || p[0]?.packCode || "";
        const nextPack = p.find((x) => x.packCode === nextCode);
        setSelectedCode(nextCode);
        if (nextPack) {
          trackPaymentEvent("opened", {
            packCode: nextPack.packCode,
            value: Number(nextPack.priceAmountMinor || 0) / 100,
            currency: nextPack.currency || "PLN"
          });
        }
      })
      .catch(() => {
        setError("Could not load payment packs.");
        trackPaymentEvent("failed");
      });
  }, [isOpen, initialPackCode]);
  const selectedPack = useMemo(() => packs.find((p) => p.packCode === selectedCode), [packs, selectedCode]);
  async function startCheckout() {
    if (!selectedPack) return;
    setLoading(true);
    setError("");
    const paymentDetails = {
      packCode: selectedPack.packCode,
      value: Number(selectedPack.priceAmountMinor || 0) / 100,
      currency: selectedPack.currency || "PLN"
    };
    trackPaymentEvent("started", paymentDetails);
    try {
      const successUrl = `${window.location.origin}/payment/return?session_id={CHECKOUT_SESSION_ID}`;
      try {
        sessionStorage.setItem("paymentReturnTo", window.location.pathname + window.location.search);
      } catch (e) {
        /* private mode */
      }
      const r = await createCheckoutSession(selectedPack.packCode, successUrl, window.location.href);
      setClientSecret(r.data?.clientSecret || "");
      if (!r.data?.clientSecret) {
        setError("Checkout could not be started.");
        trackPaymentEvent("failed", paymentDetails);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Could not start checkout.");
      trackPaymentEvent("failed", paymentDetails);
    } finally {
      setLoading(false);
    }
  }
  function handleClose() {
    if (clientSecret) trackPaymentEvent("cancelled", { packCode: selectedPack?.packCode });
    trackModalEvent("closed", "buy_units");
    onClose();
  }
  if (!isOpen) return null;
  const polish = locale === "pl";
  const stripeUnavailable = stripeStatus === "failed";
  return <div className="buy-units-overlay"><div className="buy-units-modal" role="dialog" aria-modal="true"><button type="button" className="buy-units-close" onClick={handleClose} aria-label={polish ? "Zamknij" : "Close"}>×</button><h2 className="buy-units-title">{polish ? "Kup Tokeny" : "Buy Tokens"}</h2>{!clientSecret ? <><p className="buy-units-description">{polish ? "Wybierz pakiet. Ceny zawierają VAT (brutto)." : "Choose a configured pack. Prices include VAT (gross)."}</p><div className="buy-units-pack-list">{packs.map((p) => <label className={`buy-units-pack ${selectedCode === p.packCode ? "selected" : ""}`} key={p.packCode}><input type="radio" name="payment-pack" checked={selectedCode === p.packCode} onChange={() => setSelectedCode(p.packCode)} /><span><strong>{p.title || p.packCode}</strong><small>{p.kind === "MATCH_REFILL" || p.packCode === "MATCH_REFILL" ? (polish ? "+20 dopasowań do dziennego limitu" : "+20 matches to your daily limit") : `${p.unitsBase} + ${p.unitsBonus || 0} ${polish ? "gratis" : "free"} = ${p.unitsTotal} ${polish ? "Tokenów" : "Tokens"}`}</small>{p.kind !== "MATCH_REFILL" && p.packCode !== "MATCH_REFILL" && (p.approximateUse || p.messagesEquivalent) && <small className="buy-units-approx">{p.approximateUse || `≈${p.messagesEquivalent} messages`}</small>}{p.badge && <small className="buy-units-badge">{p.badge}</small>}</span><b>{price(p)}</b></label>)}</div>{selectedPack && <div className="buy-units-total">{polish ? "Łącznie brutto" : "Gross total"}: <strong>{price(selectedPack)}</strong></div>}{error && <div className="buy-units-error">{error}</div>}<button type="button" className="buy-units-submit" disabled={loading || !selectedPack || stripeStatus !== "ready"} onClick={startCheckout}>{loading ? (polish ? "Ładowanie..." : "Loading...") : (polish ? "Przejdź do bezpiecznej płatności" : "Continue to secure checkout")}</button>{stripeStatus === "unconfigured" && <div className="buy-units-error">{polish ? "Płatności Stripe nie są skonfigurowane." : "Stripe checkout is not configured."}</div>}{stripeUnavailable && <div className="buy-units-error">{polish ? "Nie można teraz załadować płatności Stripe. Spróbuj ponownie później." : "Stripe checkout is temporarily unavailable. Please try again later."}</div>}</> : <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}><EmbeddedCheckout /></EmbeddedCheckoutProvider>}</div></div>;
}
export default BuyUnitsModal;
